<template>
    <div class="timeout-wrapper blitz-mode" :class="{['is-mobile']: isMobile}">
        <div
            class="timeout-widget"
            :class="{
                ['timed-out']: timedOut,
                ['clickable']: !isActivePlayer,
                ['always-show']: isBlitz,
            }"
            @click="openModal()"
        >
            <fa-icon class="timeout-icon" icon="hourglass" />
            <div class="time-limits" v-if="(!timedOut || (timedOut && isActivePlayer) || isSpectator)">
                <div
                    v-for="(player) in players"
                    class="time-limit"
                    :class="{['alert']: (activePlayer && player.id === activePlayer.id ?
                        getTimeRemaining(activePlayer.timeLeft) :
                         player.timeLeft
                    ) <= 60000}"
                    :key="`hex-${player.id}`"
                >
                    <royal-coin :color="player.team.color" :theme="theme" v-if="isBlitz"/>
                    <span class="time">{{ formatTimeRemaining(timeRemaining[player.id]) }}</span>
                    <span
                        class="time-increment-alert"
                        :class="{['animate']: timeIncrementAlert[player.id] }"
                         v-if="isBlitz"
                    >
                        +3
                    </span>
                </div>
            </div>
            <span class="claim-victory" v-if="timedOut && !activeTeam && !isSpectator">
                Claim Victory
            </span>
        </div>
    </div>

</template>

<script>
import moment from 'moment';
import {
    NO_TIME_REMAINING,
    formatTimeRemaining,
} from './TimeoutBlitz-helpers';

export default {
    props: [
        'refresh',
        'players',
        'activePlayerId',
        'gameStateActivePlayerId',
        'updatedAt',
        'turn',
        'isMobile',
        'theme',
    ],
    name: 'Timeout',
    created() {
        this.startCountdown();

        for (const player of this.players) {
            this.setTimeRemaining(player.id, player.timeLeft);
        }
    },
    beforeUnmount() {
        clearInterval(this.refreshInterval);
    },
    watch: {
        refresh(current, prev) {
            if (current !== prev) {
                this.countdownAlert = false;
                this.refreshTimeout = moment().format();
                this.startCountdown();
            }
        },
        turn(current, prev) {
            if (current !== prev) {
                const previousPlayer = this.players.find(player => player.id !== this.gameStateActivePlayerId);
                this.timeIncrementAlert[previousPlayer.id] = true;

                setTimeout(() => {
                    this.timeIncrementAlert[previousPlayer.id] = false;
                }, 3000);
            }
        },
        players(current, prev) {
            if (JSON.stringify(current) !== JSON.stringify(prev)) {
                for (const player of current) {
                    this.setTimeRemaining(player.id, player.timeLeft);
                }
            }
        },
    },
    data() {
        const timeIncrementAlert = {};

        for (const player of this.players) {
            timeIncrementAlert[player.id] = false;
        }

        return {
            refreshInterval: null,
            refreshTimeout: moment().format(),
            noTimeRemaining: NO_TIME_REMAINING,
            countdownAlert: false,
            timeIncrementAlert,
            formatTimeRemaining,
        };
    },
    computed: {
        isActivePlayer() {
            return this.player.id === this.activePlayerId;
        },
        activePlayer() {
            return this.players.find(player => player.id === this.activePlayerId);
        },
        activeTeam() {
            return this.player?.team?.id === this.activePlayer?.team?.id;
        },
        gameId() {
            return parseInt(this.$route.params.id, 10);
        },
        gameState() {
            return this.$store.state.gameState;
        },
        timedOut() {
            const timeLeft = this.timeRemaining[this.activePlayer?.id];
            return typeof timeLeft !== 'undefined' && timeLeft <= 0;
        },
        player() {
            return this.$store.getters.player;
        },
        isSpectator() {
            return this.$store.getters.isSpectator;
        },
        isBlitz() {
            return this.players.length > 1;
        },
        timeRemaining() {
            return this.$store.state.timeoutBlitzState.timeRemaining;
        },
    },
    methods: {
        getTimeRemaining(timeLimitMs) {
            const now = moment();
            const lastTurnTime = moment(this.updatedAt);
            const elapsedTimeMs = now.diff(lastTurnTime, 'ms');
            const timeRemainingMs = timeLimitMs - elapsedTimeMs;

            if (timeRemainingMs <= 60000 && !this.countdownAlert) {
                this.countdownAlert = true;
            }

            return timeRemainingMs;
        },
        openModal() {
            if (this.timedOut && !this.isActivePlayer) {
                this.$store.dispatch('openModal', { type: 'timeout' });
            }
        },
        setTimeRemaining(playerId, timeRemainingMs) {
            this.$store.dispatch('setTimeRemaining', { playerId, timeRemainingMs });
        },
        startCountdown() {
            const intervalMs = 1000;

            clearInterval(this.refreshInterval);

            this.refreshInterval = setInterval(() => {
                const timeRemainingMs = this.getTimeRemaining(this.activePlayer.timeLeft);

                for (const player of this.players) {
                    if (player.id === this.activePlayer.id) {
                        this.setTimeRemaining(player.id, timeRemainingMs);
                    }
                }

                if (timeRemainingMs <= 0) {
                    clearInterval(this.refreshInterval);
                }
            }, intervalMs);
        },
    },
};
</script>

<style scoped lang="scss">
    @use "@/styles/styles.scss" as *;

    .timeout-wrapper {

        &.blitz-mode {
            display: none;

            &.is-mobile {
                display: none;
            }

            .timeout-widget {
                overflow: visible;
            }
        }

        .timeout-widget {
            display: flex;
            background: white;
            padding: 8px 10px;
            box-shadow: -1px 1px 1px 1px $medium-gray;
            border-radius: 4px;
            -webkit-user-select: none;
            -moz-user-select: none;
            -ms-user-select: none;
            user-select: none;
            transition: width .2s;

            .time-limits {
                display: flex;
            }

            > * {
                align-self: center;
            }

            &.always-show {
                overflow: visible;
                width: auto;

                .time-limit {
                    min-width: 56px;
					max-width: 56px;

                    &:nth-of-type(2) {
                        margin-right: 3px;
                    }
                }
            }

            &.timed-out {
                width: auto;
                overflow: visible;

                &.clickable {
                    cursor: pointer;
                }

                &:hover {
                    width: auto;
                }

                .time-limit {
                    color: $lancer;
                }
            }

            .time-limit {
                display: flex;
                font-family: 'RobotoSlab', 'Avenir', Helvetica, Arial, sans-serif;
                color: $gray;
                min-width: 40px;
                margin-left: 10px;
                position: relative;

                > * {
                    align-self: center;
                }

                .royal-coin {
                    max-width: 18px;
                    max-height: 18px;
                    margin-right: 3px;
                }

                .time-increment-alert {
                    background: white;
                    border: 1px solid $green;
                    border-radius: 50%;
                    box-sizing: border-box;
                    color: $green;
                    font-weight: bold;
                    opacity: 0;
                    padding: 1px 2.5px;
                    position: absolute;
                    right: -25%;
                    top: -11px;
                    transition: all 1s linear;
                    font-size: .75rem;

                    &.animate {
                        animation-duration: 1.2s;
                        animation-fill-mode: forwards;
                        animation-name: fade-in-out;
                        transform: translateY(-65%);
                    }
                }

                &.alert {
                    width: auto;
                    overflow: visible;
                    color: $lancer;
                }
            }

            .claim-victory {
                margin-left: 10px;
            }

            .timeout-icon {
                color: $sage-brown;
                font-size: 1.75em;
                display: inline-block;
                vertical-align: middle;
            }

            &:hover {
                min-width: 70.76px;
            }

        }

    }

    @media only screen and (min-width: $screen-xs) {
        .timeout-wrapper {
            &.blitz-mode {
                display: block;
            }
        }
    }
</style>
